'use client'

import { forwardRef } from 'react'
import {
  Root,
  Trigger,
  Group,
  Portal,
  Sub,
  RadioGroup,
  SubTrigger,
  SubContent,
  Content,
  Item,
  CheckboxItem,
  ItemIndicator,
  RadioItem,
  Label,
  Separator as ContextMenuSeparatorPrimitive,
} from '@radix-ui/react-context-menu'
import { Check, ChevronRight, Circle } from 'lucide-react'
import { cn } from '@/lib/utils'
const ContextMenu = Root
const ContextMenuTrigger = Trigger
const ContextMenuGroup = Group
const ContextMenuPortal = Portal
const ContextMenuSub = Sub
const ContextMenuRadioGroup = RadioGroup
const CONTEXT_MENU_MOTION_CLASS =
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=bottom]:slide-out-to-top-2 data-[side=top]:slide-in-from-bottom-2 data-[side=top]:slide-out-to-bottom-2 data-[side=left]:slide-in-from-right-2 data-[side=left]:slide-out-to-right-2 data-[side=right]:slide-in-from-left-2 data-[side=right]:slide-out-to-left-2 duration-200 ease-out-quint [will-change:opacity,transform]'
const ContextMenuSubTrigger = forwardRef(({ className, inset, children, ...props }, ref) => (
  <SubTrigger
    ref={ref}
    className={cn(
      "gap-2 rounded-sm px-2 py-1 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 flex cursor-pointer items-center outline-hidden select-none [&_svg]:pointer-events-none [&_svg]:shrink-0 relative group/context-menu-sub-trigger focus:bg-accent focus:text-accent-foreground",
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    <span className="relative z-10 flex items-center w-full">
      {children}
      <ChevronRight className="ml-auto" />
    </span>
  </SubTrigger>
))
ContextMenuSubTrigger.displayName = SubTrigger.displayName
const ContextMenuSubContent = forwardRef(({ className, children, ...props }, ref) => (
  <SubContent
    ref={ref}
    className={cn(
      CONTEXT_MENU_MOTION_CLASS,
      'ring-foreground/5 bg-popover text-popover-foreground min-w-32 rounded-md border border-border/20 p-1 shadow-md z-[999] overflow-visible origin-(--radix-context-menu-content-transform-origin)',
      className,
    )}
    {...props}
  >
    {children}
  </SubContent>
))
ContextMenuSubContent.displayName = SubContent.displayName
const ContextMenuContent = forwardRef(({ className, children, ...props }, ref) => (
  <Portal>
    <Content
      ref={ref}
      className={cn(
        CONTEXT_MENU_MOTION_CLASS,
        'ring-foreground/5 z-[999] min-w-36 rounded-md border border-border/20 bg-popover p-1 text-popover-foreground shadow-md overflow-visible origin-(--radix-context-menu-content-transform-origin)',
        className,
      )}
      {...props}
    >
      {children}
    </Content>
  </Portal>
))
ContextMenuContent.displayName = Content.displayName
const ContextMenuItem = forwardRef(({ className, inset, children, ...props }, ref) => (
  <Item
    ref={ref}
    className={cn(
      "data-[variant=destructive]:text-destructive data-[variant=destructive]:*:[svg]:text-destructive gap-2 rounded-sm px-2 py-1 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 group/context-menu-item relative flex cursor-pointer items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 focus:bg-accent focus:text-accent-foreground",
      inset && 'pl-8',
      className,
    )}
    {...props}
  >
    <span className="relative z-10 flex items-center gap-2 w-full">{children}</span>
  </Item>
))
ContextMenuItem.displayName = Item.displayName
const ContextMenuCheckboxItem = forwardRef(({ className, children, checked, ...props }, ref) => (
  <CheckboxItem
    ref={ref}
    className={cn(
      "gap-2 rounded-lg py-1 pr-7 pl-2 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-pointer items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 group/context-menu-checkbox-item",
      className,
    )}
    checked={checked}
    {...props}
  >
    <div className="absolute inset-0 bg-accent rounded-lg opacity-0 group-focus/context-menu-checkbox-item:opacity-100 transition-opacity duration-150 pointer-events-none" />
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center z-10">
      <ItemIndicator>
        <Check className="h-4 w-4" />
      </ItemIndicator>
    </span>
    <span className="relative z-10 flex items-center gap-2 w-full">{children}</span>
  </CheckboxItem>
))
ContextMenuCheckboxItem.displayName = CheckboxItem.displayName
const ContextMenuRadioItem = forwardRef(({ className, children, ...props }, ref) => (
  <RadioItem
    ref={ref}
    className={cn(
      "gap-2 rounded-lg py-1 pr-7 pl-2 text-[13px] [&_svg:not([class*='size-'])]:size-3.5 relative flex cursor-pointer items-center outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 group/context-menu-radio-item",
      className,
    )}
    {...props}
  >
    <div className="absolute inset-0 bg-accent rounded-lg opacity-0 group-focus/context-menu-radio-item:opacity-100 transition-opacity duration-150 pointer-events-none" />
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center z-10">
      <ItemIndicator>
        <Circle className="h-2 w-2 fill-current" />
      </ItemIndicator>
    </span>
    <span className="relative z-10 flex items-center gap-2 w-full">{children}</span>
  </RadioItem>
))
ContextMenuRadioItem.displayName = RadioItem.displayName
const ContextMenuLabel = forwardRef(({ className, inset, ...props }, ref) => (
  <Label
    ref={ref}
    className={cn(
      'text-muted-foreground px-2 py-1 text-[11px] font-semibold tracking-wider',
      inset && 'pl-8',
      className,
    )}
    {...props}
  />
))
ContextMenuLabel.displayName = Label.displayName
const ContextMenuSeparator = forwardRef(({ className, ...props }, ref) => (
  <ContextMenuSeparatorPrimitive
    ref={ref}
    className={cn('bg-border/10 -mx-1 my-1 h-px', className)}
    {...props}
  />
))
ContextMenuSeparator.displayName = ContextMenuSeparatorPrimitive.displayName
const ContextMenuShortcut = ({ className, ...props }) => {
  return <div className={cn('ml-auto text-xs text-muted-foreground', className)} {...props} />
}
ContextMenuShortcut.displayName = 'ContextMenuShortcut'
export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuCheckboxItem,
  ContextMenuRadioItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuGroup,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuRadioGroup,
}
